import { BorderRadius, Spacing } from '@persona-id/design-tokens';
import { CircleHelpIcon } from '@persona-id/design-tokens/icons';
import { capitalize, groupBy, isEmpty, uniqBy } from 'lodash/fp';
import { createRef, useState } from 'react';
import styled from 'styled-components';

import AttributeItem, { AttributeGrid } from 'dashboard/components/AttributeItem';
import ContextMenu from 'dashboard/components/ContextMenu';
import Permissioned from 'dashboard/components/Permissioned';
import IgnoreHitsModal from 'dashboard/components/ReportResult/IgnoreHitsModal';
import ReportResultLayout from 'dashboard/components/ReportResult/ReportResultLayout';
import {
  CATEGORY_ORDER,
  PersonaWatchlistMatchedEntities,
} from 'dashboard/components/ReportResult/Watchlist';
import WatchlistAccordions from 'dashboard/components/ReportResult/WatchlistAccordions';
import WatchlistPhotoComparison from 'dashboard/components/ReportResult/WatchlistPhotoComparison';
import AccordionInline from 'dashboard/components-lib/AccordionInline';
import { ProductFeatureName } from 'dashboard/models/organization';
import {
  ReportWatchlist,
  SourceMatchEntity,
  translateWatchlistBirthdates,
  translateWatchlistLocations,
  translateWatchlistType,
  WatchlistIdentification,
  WatchlistIdentificationType,
  WatchlistMatch,
  WatchlistType,
} from 'dashboard/models/report/watchlist';
import { ReportSnapshotWatchlist } from 'dashboard/models/report-snapshot';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import TabBar, { Tab } from 'design-system/components/TabBar';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { FontWeight } from 'design-system/variables/text';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import { useIsOverflow } from 'lib/hooks/useIsOverflow';
import useProductFeature from 'lib/hooks/useProductFeature';
import useToggle from 'lib/hooks/useToggle';
import { Permission } from 'lib/permissions';

const TitleContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const RegularWeightTitleContainer = styled(TitleContainer)`
  font-weight: ${FontWeight.Regular};
`;

const TabText = styled.div`
  font-weight: ${FontWeight.Medium};
  & > span {
    margin-right: ${Spacing[2]};
  }
`;

const DetailsSection = styled.div<{ isExpanded: boolean }>`
  padding: ${Spacing[4]};
  overflow: hidden;
  ${({ isExpanded }) => (isExpanded ? '' : `max-height: 200px`)};

  box-sizing: border-box;
  border: 1px solid ${({ theme }) => theme.color.border.secondary};
  border-radius: ${BorderRadius.Md};

  & > *:not(:last-child) {
    display: block;
    margin-bottom: ${Spacing[4]};
  }
`;

const StyledCardTitle = styled(Card.Title)`
  padding-bottom: 0;
`;

const ExpandText = styled.div<{
  disabled?: boolean;
}>`
  cursor: pointer;
  color: blue;
`;

interface PersonaSourceEntityInfoProps {
  report: ReportWatchlist | ReportSnapshotWatchlist;
  entity: SourceMatchEntity;
  multiCardFormat: boolean;
  isIgnoredEntity?: boolean;
  readOnly?: boolean;
}

export function formatWatchlistIdentifications({
  type,
  number,
  country,
  comment,
}: WatchlistIdentification) {
  let title = '';
  const components: string[] = [];

  if (number == null) {
    return;
  }

  switch (type) {
    case WatchlistIdentificationType.Passport:
      title = `Passport: ${number}`;
      break;
    case WatchlistIdentificationType.IdentificationCard:
      title = `ID card: ${number}`;
      break;
    case WatchlistIdentificationType.NationalIdentification:
      title = `National ID: ${number}`;
      break;
    case WatchlistIdentificationType.Ssn:
      title = `SSN: ${number}`;
      break;
    case WatchlistIdentificationType.TaxId:
      title = `Tax ID: ${number}`;
      break;
    case WatchlistIdentificationType.Miscellaneous:
    default:
      title = `ID number: ${number}`;
  }

  if (country) {
    components.push(`Issued by: ${country}`);
  }

  if (comment) {
    components.push(`Comment: ${comment}`);
  }

  if (isEmpty(components)) {
    return title;
  }

  const titleContainer = <RegularWeightTitleContainer>{title}</RegularWeightTitleContainer>;

  return (
    <AccordionInline title={titleContainer} defaultExpanded={false}>
      <ul>
        {components.map((description, idx) => (
          <li key={idx}>{description}</li>
        ))}
      </ul>
    </AccordionInline>
  );
}

export const WatchlistEntityMatchDetails = ({
  report,
  entity,
  multiCardFormat = false,
  isIgnoredEntity = false,
  readOnly = false,
}: PersonaSourceEntityInfoProps) => {
  const [ignoreEntityModal, { enable: showIgnoreEntityModal, disable: hideIgnoreEntityModal }] =
    useToggle(false);
  const {
    id,
    names,
    aliases,
    birthdates,
    locations,
    identifications,
    sex,
    emails,
    'phone-numbers': phoneNumbers,
    titles,
    details,
    images,
    watchlists,
  } = entity;
  const ignoreList = entity['ignore-list'] || [];
  const [isActiveTabSelected, setIsActiveTabSelected] = useState(true);
  const [isExpanded, { toggle }] = useToggle(false);
  const {
    residency: residency,
    birthplace: birthplace,
    nationality: nationality,
    citizenship: citizenship,
  } = groupBy('type', locations);
  const profileImageUrl = report.attributes['profile-image-url'];
  const primaryName = names?.[0] ?? '';
  const processedAliases = uniqBy((alias) => alias.toLowerCase(), [
    ...(names?.slice(1) ?? []),
    ...(aliases ?? []),
  ] as string[]);
  const groupedMatches = isActiveTabSelected
    ? groupBy('list-type', watchlists)
    : groupBy(
        'list-type',
        ignoreList.map((entry) => entry.match),
      );
  const listsToDisplay = new Set(Object.keys(groupedMatches));
  const detailsSection = createRef<HTMLDivElement>();
  const isOverflow = useIsOverflow(detailsSection);
  const enableBiometrics = useFeatureFlag('report:enable-biometric-watchlists');
  const hasWatchlistPortraitsEnabled = useProductFeature(
    ProductFeatureName.ReportsWatchlistPortraits,
  );
  const referenceImages = (images || []).filter((image) => !isEmpty(image['internal-url']));

  const matches =
    Object.keys(groupedMatches).length > 0 ? (
      <WatchlistAccordions<WatchlistMatch, WatchlistType>
        prefixKey={
          isActiveTabSelected ? `active-${watchlists?.length}` : `dismissed-${ignoreList.length}`
        }
        groupedMatches={groupedMatches}
        listOrder={CATEGORY_ORDER.filter((category) => listsToDisplay.has(category))}
        title={translateWatchlistType}
        subtitle={(match: WatchlistMatch) => (
          <PersonaWatchlistMatchedEntities entities={match.entities} />
        )}
      />
    ) : (
      <Paragraph>No {isActiveTabSelected ? 'active' : 'dismissed'} matches</Paragraph>
    );

  return (
    <ReportResultLayout multiCardFormat={multiCardFormat}>
      <>
        <TitleContainer>
          <Card.Title>
            {multiCardFormat && (
              <>
                Identity Match - {primaryName} <Spacer width={1} />
              </>
            )}
            {isIgnoredEntity && <Sticker variant='danger'>DISMISSED</Sticker>}
          </Card.Title>
          {multiCardFormat && (
            <Permissioned permission={Permission.ReportIgnoreHitsWrite}>
              <ContextMenu
                variant='inverse'
                items={[
                  {
                    id: 'reports-ignore-hits-modal__dismiss-hits-button',
                    onClick: showIgnoreEntityModal,
                    hidden: false,
                    label: 'Dismiss identity match',
                  },
                ]}
                renderItem={(item) => (
                  <ContextMenu.Item key={item.id} id={item.id} onClick={item.onClick}>
                    {item.label}
                  </ContextMenu.Item>
                )}
                disabled={readOnly || isIgnoredEntity}
              />
              <IgnoreHitsModal
                report={report}
                entityId={id}
                ignoreType='entity'
                modalHeaderText='Dismiss Identity Match?'
                ignoreHitsModalOpen={ignoreEntityModal}
                hideIgnoreHitsModal={hideIgnoreEntityModal}
              />
            </Permissioned>
          )}
        </TitleContainer>

        {multiCardFormat && (
          <>
            <Spacer height={4} /> <Card.SectionHeader>Profile</Card.SectionHeader>
          </>
        )}

        <AttributeGrid>
          {names && <AttributeItem title='Primary Name' value={primaryName} placeholder='N/A' />}
          {birthdates && (
            <AttributeItem
              title='Birthdate'
              value={translateWatchlistBirthdates(birthdates)}
              placeholder='N/A'
            />
          )}
          {sex && <AttributeItem title='Sex' value={capitalize(sex)} placeholder='N/A' />}
          {nationality && (
            <AttributeItem
              title='Nationality'
              value={nationality.map(translateWatchlistLocations)}
              placeholder='N/A'
            />
          )}
          {processedAliases && (
            <AccordionInline title={`Aliases (${processedAliases.length})`} defaultExpanded={false}>
              {processedAliases.map((alias) => (
                <div key={alias}>{alias}</div>
              ))}
            </AccordionInline>
          )}
        </AttributeGrid>

        {(hasWatchlistPortraitsEnabled || enableBiometrics) && referenceImages.length > 0 && (
          <>
            <Spacer height={4} />
            {profileImageUrl ? (
              <Card.SectionHeader>
                Portrait similarity
                <Tooltip side='top' iconTrigger={<CircleHelpIcon />}>
                  <TooltipWrapper size='sm'>
                    Portrait similarity shows whether the end user&rsquo;s portrait resembles
                    publicly available photos of someone on a watchlist.
                  </TooltipWrapper>
                </Tooltip>
              </Card.SectionHeader>
            ) : (
              <Card.SectionHeader>Portrait</Card.SectionHeader>
            )}
            <WatchlistPhotoComparison
              referenceImages={referenceImages}
              profileImageUrl={profileImageUrl}
            />
          </>
        )}

        <Spacer height={4} />
        <Card.SectionHeader>Additional Info</Card.SectionHeader>
        <AttributeGrid>
          {titles && <AttributeItem title='Titles' value={titles} placeholder='N/A' />}
          {emails && <AttributeItem title='Emails' value={emails} placeholder='N/A' />}
          {birthplace && (
            <AttributeItem
              title='Birthplace'
              value={birthplace.map(translateWatchlistLocations)}
              placeholder='N/A'
            />
          )}
          {residency && (
            <AttributeItem
              title='Residency'
              value={residency.map(translateWatchlistLocations)}
              placeholder='N/A'
            />
          )}
          {citizenship && (
            <AttributeItem
              title='Citizenship'
              value={citizenship.map(translateWatchlistLocations)}
              placeholder='N/A'
            />
          )}
          {!isEmpty(identifications) && (
            <AttributeItem
              title='IDs'
              value={identifications?.map(formatWatchlistIdentifications).filter(Boolean)}
              placeholder='N/A'
            />
          )}
          {phoneNumbers && (
            <AttributeItem title='Phone Numbers' value={phoneNumbers} placeholder='N/A' />
          )}
        </AttributeGrid>
        {details && details.length > 0 && (
          <>
            <Spacer height={4} />
            <Card.SectionHeader>Listing Details</Card.SectionHeader>
            <DetailsSection isExpanded={isExpanded} ref={detailsSection}>
              {details.map((e, id) => (
                <p key={id}>{e.detail}</p>
              ))}
            </DetailsSection>
            {(isOverflow || isExpanded) && (
              <ExpandText id='watchlist-detail__expand' onClick={() => toggle()}>
                {isExpanded ? 'Show less' : 'Show more'}
              </ExpandText>
            )}
          </>
        )}
        <Spacer height={4} />
        <StyledCardTitle>Matches</StyledCardTitle>
        {ignoreList.length > 0 && (
          <>
            <TabBar>
              <Tab
                id='report-watchlist__ignored-tab-active'
                selected={isActiveTabSelected}
                onClick={() => {
                  setIsActiveTabSelected(true);
                }}
              >
                <TabText>
                  <span>Active</span>
                  <Sticker>{watchlists?.length}</Sticker>
                </TabText>
              </Tab>
              <Tab
                id='report-watchlist__ignored-tab-ignored'
                selected={!isActiveTabSelected}
                onClick={() => {
                  setIsActiveTabSelected(false);
                }}
              >
                <TabText>
                  <span>Dismissed</span>
                  <Sticker>{ignoreList.length}</Sticker>
                </TabText>
              </Tab>
            </TabBar>
            <Spacer height={2} />
          </>
        )}
        {matches}
      </>
    </ReportResultLayout>
  );
};

const TooltipWrapper = styled(Paragraph)`
  width: 300px;
`;

export default WatchlistEntityMatchDetails;
